import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  TextField,
  Chip,
  Box,
  Divider,
  Breadcrumbs,
  Button,
  Switch,
  FormControlLabel,
  Avatar,
  IconButton
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import { useForm, Controller } from 'react-hook-form';
import { LoadingButton } from '@mui/lab'
import { useTranslation } from 'react-i18next';
import DeleteUserModal from '../../components/specific/modal/DeleteUser';
import { ProfileFormData } from '../../interfaces/SettingsProp';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { PIN_TYPES_OPTIONS } from '../../enums/MarkerOptions';
import mbxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import Select, { SingleValue } from 'react-select';
import { PlaceOption } from '../../interfaces/Mapbox';
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import { phoneInputStyles } from './phoneInputStyle';
import PinnerProModal from '../../components/specific/modal/PinnerProModal';
import { PinnerProIcon } from '../../assets/icons/Index';
import { AppRoutes } from '../../enums/Approutes';
import { useToast } from '../../hooks/useToast';
import ImageCropModal from '../../components/specific/modal/ImageCropModal';
import { toast } from 'react-toastify';
import { getConfig } from '../../config/env';

const CustomSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#E9495A',
    '&:hover': {
      backgroundColor: theme.palette.action.hoverOpacity,
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#E9495A',
  },
}));

const ProfileSettings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const userId = sessionStorage.getItem('userId');
  const authToken = sessionStorage.getItem('authToken');
  const url = getConfig().BASE_URL;
  const geocodingClient = mbxGeocoding({ accessToken: getConfig().MAPBOX_TOKEN! });
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openBecomeProModal, setOpenBecomeProModal] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [placeOptions, setPlaceOptions] = useState<PlaceOption[]>([]);
  const [isPinnerPro, setIsPinnerPro] = useState<boolean>(false);
  const { photoError, photoSuccess, photoDeletedSuccess, photoDeletedError, settingsSuccess, settingsError } = useToast();
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);


  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = phoneInputStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
  } = useForm<ProfileFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      birth_date: new Date(),
      residence: '',
      public_residence: false,
      profile_picture: null,
      interested_to: [],
      phone_number: '',
      public_phone_number: false,
      bio: ''
    }
  });

  const headers = useMemo(() => ({
    'Authorization': `Bearer ${authToken}`
  }), [authToken]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${url}/users/auth/profile/`, { headers });
        const userData = response.data;
        setValue('first_name', userData.first_name);
        setValue('last_name', userData.last_name);
        setValue('email', userData.email);
        setValue('birth_date', userData.birth_date);
        setValue('residence', userData.residence);
        setValue('public_residence', userData.public_residence);
        setValue('interested_to', userData.interested_to);
        setValue('profile_picture', userData.profile_picture);
        setValue('phone_number', userData.phone_number);
        setValue('public_phone_number', userData.public_phone_number);
        setValue('bio', userData.bio);
        setFirstName(userData.first_name);
        setLastName(userData.last_name);
        setProfilePicture(userData.profile_picture);
        setIsPinnerPro(userData.is_pinner_pro);
      } catch (error) {
        console.error('Errore nel recupero dei dati utente', error);
      }
    };

    fetchUserData();
  }, [setValue, userId, headers]);

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const fetchPlaceSuggestions = async (inputValue: string) => {
    try {
      const response = await geocodingClient
        .forwardGeocode({
          query: inputValue,
          autocomplete: true,
          limit: 5,
          language: [i18n.language]
        })
        .send();

      const fetchedOptions = response.body.features.map((feature) => ({
        label: feature.place_name,
        value: feature.place_name,
        center: feature.center as [number, number],
      }));

      setPlaceOptions(fetchedOptions);
    } catch (err) {
      console.error('Errore fetchPlaceSuggestions:', err);
    }
  };

  const handleProfilePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Validazione dimensione (5 MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      photoError();
      toast.error(t('crop_image:file_too_large'));
      event.target.value = '';
      return;
    }

    // Validazione formato
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      photoError();
      toast.error(t('crop_image:invalid_format'));
      event.target.value = '';
      return;
    }

    // Apri modal di crop
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setOriginalFile(file);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteProfilePicture = async () => {
    setProfilePicture(null);
    setValue('profile_picture', null);

    try {
      const formData = new FormData();
      formData.append('profile_picture', '');

      await axios.patch(`${url}/users/auth/profile/`, formData, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      window.dispatchEvent(new Event('profilePictureUpdated'));
      photoDeletedSuccess();
    } catch (error) {
      console.error('Errore nella cancellazione della foto del profilo', error);
      photoDeletedError();
    }
  };

  const handleCropConfirm = async (compressedFile: File) => {
    try {
      if (profilePicture) {
        const deleteFormData = new FormData();
        deleteFormData.append('profile_picture', '');
        await axios.patch(`${url}/users/auth/profile/`, deleteFormData, { headers });
      }

      setValue('profile_picture', compressedFile);

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicture(reader.result as string);
      };
      reader.readAsDataURL(compressedFile);

      const formData = new FormData();
      formData.append('profile_picture', compressedFile);

      await axios.patch(`${url}/users/auth/profile/`, formData, { headers });

      window.dispatchEvent(new Event('profilePictureUpdated'));
      photoSuccess();

      setCropModalOpen(false);
      setImageToCrop(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Errore upload:', error);
      photoError();
    }
  };

  const handleCropCancel = () => {
    setCropModalOpen(false);
    setImageToCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data: any) => {
    let residenceEnglish = data.residence;
    try {
      setLoading(true);

      const enResponse = await geocodingClient
        .forwardGeocode({
          query: data.residence,
          limit: 1,
          language: ['en']
        })
        .send();

      if (enResponse.body.features && enResponse.body.features.length > 0) {
        residenceEnglish = enResponse.body.features[0].place_name;
      }

      const formData = {
        residence: residenceEnglish,
        public_residence: data.public_residence,
        phone_number: data.phone_number,
        public_phone_number: data.public_phone_number,
        interested_to: data.interested_to,
        bio: data.bio
      };

      await axios.patch(`${url}/users/auth/profile/`, formData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
      });

      setLoading(false);
      settingsSuccess();
    } catch (error) {
      console.error('Errore nell\'aggiornamento dei dati utente', error);
      console.error('Errore durante la conversione di residence in inglese:', error);
      settingsError();
    }
    setLoading(false);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleBecomeProModal = () => {
    setOpenBecomeProModal(true);
  };

  const handleCloseBecomeProModal = () => {
    setOpenBecomeProModal(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Container maxWidth="lg" className="mt-8">
        <Breadcrumbs aria-label="breadcrumb" className="mb-4">
          <Typography className='text-text-tertiary text-xl' >
            {t('settings:breadcrumbs_title')}
          </Typography>
          <Typography className='text-text-primary font-bold text-md'>{t('settings:breadcrumbs_subtitle')}</Typography>
        </Breadcrumbs>
        <Divider className="my-6" />

        <Paper elevation={0} className="p-0">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              <Grid size={{ xs: 12, md: 9 }} className="bg-white p-4">
                <div className="w-full flex flex-col gap-6 md:flex-row md:gap-0 items-start md:items-center justify-between mb-8 relative">
                  <div className="flex items-center">
                    <div className="relative group">
                      <Avatar
                        src={profilePicture ?? undefined}
                        alt={`${firstName} ${lastName}`}
                        sx={{ width: 70, height: 70, cursor: 'pointer' }}
                        onClick={handleAvatarClick}
                      />
                      {isPinnerPro && (
                        <div style={{ position: 'absolute', bottom: -5, right: -5, zIndex: 10 }}>
                          <PinnerProIcon className='' width={27} height={27} />
                        </div>
                      )}
                      <div
                        className="w-full absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black bg-opacity-5 rounded-full cursor-pointer"
                      >
                        <IconButton
                          size="large"
                          onClick={handleAvatarClick}
                          sx={{ width: '100%', height: '100%' }}
                        >
                          <AddIcon sx={{ color: '#E9495A' }} fontSize='large' />
                        </IconButton>
                      </div>
                      {profilePicture && (
                        <IconButton
                          size="small"
                          onClick={handleDeleteProfilePicture}
                          sx={{ position: 'absolute', top: -8, right: -8, backgroundColor: '#fff' }}
                        >
                          <DeleteIcon fontSize="small" sx={{ color: '#E9495A' }} />
                        </IconButton>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        onChange={handleProfilePictureChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className='ml-4'>
                      <Typography variant="h6" className="font-bold flex items-center">
                        {firstName} {lastName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {t('settings:settings_guide')}
                      </Typography>
                    </div>
                  </div>
                  <div className='w-full md:w-auto'>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        backgroundColor: '#E9495A',
                        '&:hover': {
                          backgroundColor: '#E6263B',
                        }
                      }}
                      onClick={isPinnerPro ? () => navigate(AppRoutes.PINNER_PRO_PROFILE.replace(':userId', userId ?? '')) : handleBecomeProModal}
                    >
                      {isPinnerPro ? t('settings:view_profile') : t('settings:pinner_pro')}
                    </Button>
                  </div>
                </div>

                <Divider className="my-6" />

                <Grid container spacing={3} className='w-full flex items-center justify-between'>

                  {/* First Name */}
                  <Grid size={{ xs: 12 }} className="w-full flex flex-col md:flex-row items-start md:items-center mb-1">
                    <Typography variant="body1" className="w-full md:w-1/2 font-bold text-sm mb-2 md:mb-0">{t('settings:first_name')}</Typography>
                    <Controller
                      name="first_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          variant="outlined"
                          value={field.value ?? ''}
                          disabled
                          slotProps={{ input: { readOnly: true } }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Last Name */}
                  <Grid size={{ xs: 12 }} className="w-full flex flex-col md:flex-row items-start md:items-center mb-1">
                    <Typography variant="body1" className="w-full md:w-1/2 font-bold text-sm mb-2 md:mb-0">{t('settings:last_name')}</Typography>
                    <Controller
                      name="last_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          variant="outlined"
                          value={field.value ?? ''}
                          disabled
                          slotProps={{ input: { readOnly: true } }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid size={{ xs: 12 }} className="w-full flex flex-col md:flex-row items-start md:items-center mb-1">
                    <Typography variant="body1" className="w-full md:w-1/2 font-bold text-sm mb-2 md:mb-0">{t('settings:email')}</Typography>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          variant="outlined"
                          value={field.value ?? ''}
                          disabled
                          slotProps={{ input: { readOnly: true } }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Birth Date */}
                  <Grid size={{ xs: 12 }} className="w-full flex flex-col md:flex-row items-start md:items-center mb-1">
                    <Typography variant="body1" className="w-full md:w-1/2 font-bold text-sm mb-2 md:mb-0">
                      {t('settings:birth_date')}
                    </Typography>
                    <Controller
                      name="birth_date"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          type='date'
                          fullWidth
                          disabled
                          variant='outlined'
                          slotProps={{ input: { readOnly: true } }}
                        />
                      )}
                    />
                  </Grid>

                  <Typography variant="subtitle1" className="w-full font-regular text-text-secondary text-sm mt-8">
                    {t('settings:bio')}
                  </Typography>
                  {/* Bio */}
                  <Grid size={{ xs: 12 }} className="w-full flex flex-col md:flex-row items-start md:items-center mb-1">
                    <Typography variant="body1" className="w-full md:w-1/2 font-bold text-sm mb-2 md:mb-0">{t('settings:bio_label')}</Typography>
                    <Controller
                      name="bio"
                      control={control}
                      rules={{ maxLength: 500 }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const value = e.target.value.slice(0, 500);
                            field.onChange(value);
                          }}
                          variant="outlined"
                          fullWidth
                          multiline
                          placeholder={t('settings:bio_placeholder')}
                          rows={4}
                          margin="none"
                          helperText={`${field.value?.length ?? 0}/500`}
                          slotProps={{
                            inputLabel: { shrink: true }
                          }}
                          sx={{
                            margin: 0,
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#E0E0E0',
                              },
                              '&:hover fieldset': {
                                borderColor: '#E0E0E0',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#E0E0E0',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: '#000000',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#000000',
                            },
                            '& .MuiInputBase-input.Mui-disabled': {
                              color: '#000000',
                            },
                            '& .MuiFormHelperText-root': {
                              textAlign: 'right',
                              marginRight: '5px',
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Typography variant="subtitle1" className="w-full font-regular text-text-secondary text-sm mt-6">
                    {t('settings:optional_info')}
                  </Typography>
                  {/* Phone Number */}
                  <Grid size={{ xs: 12 }} className="w-full flex flex-col md:flex-row items-start md:items-center mb-1">
                    <Typography variant="body1" className="w-full md:w-1/2 font-bold text-sm mb-2 md:mb-0">{t('settings:phone_number')}</Typography>
                    <div className="w-full flex items-center">
                      <Controller
                        name="phone_number"
                        control={control}
                        rules={{
                          validate: (value) => {
                            if (!value) return true;
                            const phoneRegex = /^\+?[1-9]\d{1,14}$/;
                            return phoneRegex.test(value) || t('welcome:phone_number_invalid');
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <div style={{
                            width: '100%',
                            marginRight: window.innerWidth >= 600 ? '2rem' : '1rem',
                            position: 'relative'
                          }}>
                            <PhoneInput
                              defaultCountry="IT"
                              value={field.value ?? ''}
                              onChange={(value) => field.onChange(value || '')}
                              placeholder={t('settings:phone_placeholder')}
                              className={`phone-input ${fieldState.error ? 'error' : ''}`}
                            />
                            {fieldState.error && (
                              <Typography
                                color="error"
                                variant="caption"
                                sx={{
                                  position: 'absolute',
                                  bottom: '-20px',
                                  left: '0',
                                  fontSize: '0.75rem'
                                }}
                              >
                                {fieldState.error.message}
                              </Typography>
                            )}
                          </div>
                        )}
                      />
                      <Controller
                        name="public_phone_number"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color='error'
                              />
                            }
                            label={field.value ? <LockOpenIcon /> : <LockIcon />}
                          />
                        )}
                      />
                    </div>
                  </Grid>

                  {/* Residence */}
                  <Grid size={{ xs: 12 }} className="w-full flex flex-col md:flex-row items-start md:items-center mb-1">
                    <Typography variant="body1" className="w-full md:w-1/2 font-bold text-sm mb-2 md:mb-0">{t('settings:residence')}</Typography>
                    <div className="w-full flex items-center">
                      <Controller
                        name="residence"
                        control={control}
                        rules={{ required: t('settings:residence_required') }}
                        render={({ field }) => {
                          const selected =
                            field.value
                              ? placeOptions.find(opt => opt.value === field.value) ??
                              { label: field.value, value: field.value }
                              : null;
                          return (
                            <Select<PlaceOption>
                              placeholder={t('settings:residence_placeholder')}
                              inputId="residence-select"
                              isClearable
                              menuPortalTarget={document.body}
                              options={placeOptions}
                              value={selected}
                              onInputChange={(inputValue) => {
                                if (inputValue) {
                                  fetchPlaceSuggestions(inputValue);
                                }
                              }}
                              onChange={(opt: SingleValue<PlaceOption>) => {
                                if (opt) {
                                  field.onChange(opt.value);
                                } else {
                                  field.onChange('');
                                }
                              }}
                              styles={{
                                menuPortal: base => ({ ...base, zIndex: 9999 }),
                                control: base => ({ ...base, height: '3.5rem', overflow: 'hidden' }),
                                container: base => ({ ...base, width: '100%', marginRight: '2rem' })
                              }}
                            />
                          )
                        }}
                      />
                      <Controller
                        name="public_residence"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <CustomSwitch
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                                color='error'
                              />
                            }
                            label={field.value ? <LockOpenIcon /> : <LockIcon />}
                          />
                        )}
                      />
                    </div>
                  </Grid>
                </Grid>

                <Divider className="my-6" />

                <Typography variant="subtitle1" className="w-full font-bold text-sm mb-4">
                  {t('settings:traveler_type')}
                </Typography>
                <Controller
                  name="interested_to"
                  control={control}
                  defaultValue={[]}
                  rules={{ required: t('welcome:interested_to_required') }}
                  render={({ field, fieldState }) => {
                    const { value, onChange } = field;
                    return (
                      <Box className="flex flex-wrap gap-4">
                        {PIN_TYPES_OPTIONS.map((option) => {
                          const isSelected = value.includes(option.value);
                          return (
                            <Chip
                              key={option.value}
                              label={
                                <span>
                                  {option.icon} {t(option.labelKey)}
                                </span>
                              }
                              onClick={() => {
                                const currentIndex = value.indexOf(option.value);
                                let newSelected = [...value];

                                if (currentIndex === -1) {
                                  newSelected.push(option.value);
                                } else {
                                  newSelected.splice(currentIndex, 1);
                                }
                                onChange(newSelected);
                              }}
                              variant={isSelected ? 'filled' : 'outlined'}
                              sx={{
                                ...(isSelected && {
                                  backgroundColor: '#EBEBEB',
                                  color: '#00000',
                                  '&:hover': {
                                    backgroundColor: '#cecece',
                                  },
                                }),
                              }}
                            />
                          );
                        })}
                        {fieldState.error && (
                          <Typography color="error" variant="body2">
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </Box>
                    );
                  }}
                />
                <Divider className="my-6" />
                <div className='flex gap-6'>
                  <LoadingButton type='submit' variant="contained" loading={loading} className='bg-primary hover:bg-primary-hover'>
                    {t('common:save')}
                  </LoadingButton>
                </div>
                <Divider className="mt-6 mb-12" />
                <div>
                  <Typography variant='body1'>
                    {t('settings:delete_question')}
                  </Typography>
                  <Button
                    onClick={handleOpenModal}
                    sx={{ padding: 0, margin: '1rem auto', fontSize: { xs: '0.875rem', sm: '0.9rem' }, backgroundColor: 'trasparent', color: '#101828', fontWeight: 'bold', textTransform: 'initial', '&:hover': { backgroundColor: 'trasparent' } }}
                  >
                    {t('settings:delete_account')}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>

      <DeleteUserModal
        open={openModal}
        onClose={handleCloseModal}
      />

      <PinnerProModal
        open={openBecomeProModal}
        handleClose={handleCloseBecomeProModal}
      />

      <ImageCropModal
        open={cropModalOpen}
        imageSrc={imageToCrop}
        originalFile={originalFile}
        onClose={handleCropCancel}
        onConfirm={handleCropConfirm}
      />

    </div >
  );
}

export default ProfileSettings